// Conciliación de cierres de caja: SISTEMA vs CAJERO vs BANCO.
// POS → Venta / Caja → Conciliación de cierres.
// · Diferencias dentro de la tolerancia se auto-resuelven al cerrar.
// · Sobre la tolerancia: un admin decide caso por caso (rol del cajero / gasto / ingreso).
// · El depósito (multi-cierre) genera el movimiento en Control Bancario y descarga
//   el Efectivo en tránsito.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Loader2, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, fieldCls, money, pos, posRpc, fechaLocal } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Cierre {
  sesion_id: string; codigo: string; caja: string; cajero: string | null;
  fecha_apertura: string; fecha_cierre: string;
  fondo_inicial: number; efectivo_sistema: number; efectivo_contado: number; diferencia: number;
  total_ventas: number; a_depositar: number;
  resolucion: string | null; resolucion_obs: string | null; resuelto_por: string | null;
  deposito: { id: string; codigo: string; fecha: string; referencia: string | null; monto: number; monto_esperado: number; diferencia: number; banco: string | null } | null;
  estado_conciliacion: 'dif_arqueo_pendiente' | 'pendiente_deposito' | 'dif_deposito' | 'cuadrado';
}
interface CuentaBancaria { id: string; numero: string; alias: string | null; banco: { nombre: string } | null }
interface ConciliacionCfg { id: string; tolerancia: number }

const ESTADO: Record<Cierre['estado_conciliacion'], { label: string; cls: string }> = {
  dif_arqueo_pendiente: { label: 'Dif. arqueo pendiente', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  pendiente_deposito: { label: 'Pendiente de depósito', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  dif_deposito: { label: 'Dif. en depósito', cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  cuadrado: { label: 'Cuadrado', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
};
const RESOLUCION_LABEL: Record<string, string> = {
  tolerancia: 'Tolerancia (auto)', gasto: 'Asumido como gasto', ingreso: 'Otros ingresos', rol: 'Descuento por rol',
};

const hoy = () => fechaLocal();
const hace30 = () => fechaLocal(new Date(Date.now() - 30 * 86400000));

export function ConciliacionCierres() {
  const qc = useQueryClient();
  const [desde, setDesde] = useState(hace30());
  const [hasta, setHasta] = useState(hoy());
  const [resolver, setResolver] = useState<Cierre | null>(null);
  const [depositar, setDepositar] = useState(false);

  const { data: filas = [], isFetching } = useQuery<Cierre[]>({
    queryKey: ['pos-conciliacion', desde, hasta],
    queryFn: () => posRpc('pos_conciliacion_listar', { _desde: desde || null, _hasta: hasta || null }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: cfg = [] } = useQuery<ConciliacionCfg[]>({
    queryKey: ['pos-conc-config'], queryFn: () => pos.list<ConciliacionCfg>('conciliacion_config', '?select=id,tolerancia'), staleTime: 0,
  });
  const tolerancia = cfg[0]?.tolerancia ?? 0.05;
  const [tolEdit, setTolEdit] = useState<string | null>(null);

  const guardarTol = useMutation({
    mutationFn: async (v: number) => cfg[0] ? pos.update('conciliacion_config', cfg[0].id, { tolerancia: v }) : pos.create('conciliacion_config', { tolerancia: v }),
    onSuccess: () => { toast.success('Tolerancia actualizada'); setTolEdit(null); void qc.invalidateQueries({ queryKey: ['pos-conc-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const invalidar = () => void qc.invalidateQueries({ queryKey: ['pos-conciliacion'] });
  const pendientesDeposito = useMemo(() => filas.filter((f) => !f.deposito && f.resolucion !== null), [filas]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Scale className="w-5 h-5" /> Conciliación de cierres de caja</h1>
        <div className="flex items-end gap-2">
          <Campo label="Desde"><input type="date" className={`${fieldCls} h-9`} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={`${fieldCls} h-9`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Campo label="Tolerancia ($)">
            {tolEdit === null ? (
              <button className={`${fieldCls} h-9 w-24 text-right hover:border-primary/60`} title="Clic para editar" onClick={() => setTolEdit(String(tolerancia))}>{money(tolerancia)}</button>
            ) : (
              <input type="number" step="0.01" min="0" autoFocus className={`${fieldCls} h-9 w-24 text-right`} value={tolEdit}
                onChange={(e) => setTolEdit(e.target.value)}
                onBlur={() => guardarTol.mutate(Number(tolEdit) || 0)}
                onKeyDown={(e) => { if (e.key === 'Enter') guardarTol.mutate(Number(tolEdit) || 0); }} />
            )}
          </Campo>
          <Button onClick={() => setDepositar(true)} disabled={pendientesDeposito.length === 0}>
            <Landmark className="w-4 h-4 mr-1" /> Registrar depósito ({pendientesDeposito.length})
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Cierres ({filas.length}) {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <th className="py-2 pr-3">Cierre</th><th className="py-2 pr-3">Caja / Cajero</th><th className="py-2 pr-3">Fecha</th>
              <th className="py-2 pr-3 text-right">Sistema</th><th className="py-2 pr-3 text-right">Contado</th>
              <th className="py-2 pr-3 text-right">Dif. arqueo</th><th className="py-2 pr-3">Resolución</th>
              <th className="py-2 pr-3 text-right">A depositar</th><th className="py-2 pr-3">Depósito</th>
              <th className="py-2 pr-3 text-right">Dif. depósito</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && <tr><td colSpan={13} className="py-8 text-center text-muted-foreground">Sin cierres en el rango</td></tr>}
              {filas.map((f, i) => (
                <tr key={f.sesion_id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{f.codigo}</td>
                  <td className="py-2 pr-3">{f.caja}<div className="text-xs text-muted-foreground">{f.cajero ?? '—'}</div></td>
                  <td className="py-2 pr-3 whitespace-nowrap text-xs">{new Date(f.fecha_cierre).toLocaleString(LOCALE)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.efectivo_sistema)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.efectivo_contado)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums font-medium ${Number(f.diferencia) < 0 ? 'text-red-600' : Number(f.diferencia) > 0 ? 'text-emerald-600' : ''}`}>
                    {Number(f.diferencia) === 0 ? '—' : money(f.diferencia)}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {f.resolucion
                      ? <span title={f.resolucion_obs ?? ''}>{RESOLUCION_LABEL[f.resolucion] ?? f.resolucion}{f.resuelto_por ? ` · ${f.resuelto_por}` : ''}</span>
                      : Number(f.diferencia) !== 0 ? <span className="text-red-600 font-medium">Pendiente</span> : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.a_depositar)}</td>
                  <td className="py-2 pr-3 text-xs">
                    {f.deposito ? <span title={f.deposito.banco ?? ''}>{f.deposito.codigo} · {money(f.deposito.monto)}<div className="text-muted-foreground">{f.deposito.fecha}{f.deposito.referencia ? ` · ${f.deposito.referencia}` : ''}</div></span> : '—'}
                  </td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums ${f.deposito && Number(f.deposito.diferencia) !== 0 ? 'text-orange-600 font-medium' : ''}`}>
                    {f.deposito ? (Number(f.deposito.diferencia) === 0 ? '—' : money(f.deposito.diferencia)) : ''}
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ESTADO[f.estado_conciliacion].cls}`}>{ESTADO[f.estado_conciliacion].label}</span>
                  </td>
                  <td className="py-2 text-right">
                    {!f.resolucion && Number(f.diferencia) !== 0 && (
                      <Button size="sm" variant="outline" onClick={() => setResolver(f)}>Resolver</Button>
                    )}
                  </td>
                </tr>
              ))}
              {filas.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={4}>Totales ({filas.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(filas.reduce((a, f) => a + Number(f.efectivo_sistema || 0), 0))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(filas.reduce((a, f) => a + Number(f.efectivo_contado || 0), 0))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(filas.reduce((a, f) => a + Number(f.diferencia || 0), 0))}</td>
                  <td />
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(filas.reduce((a, f) => a + Number(f.a_depositar || 0), 0))}</td>
                  <td /><td /><td /><td />
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-muted-foreground">
            Diferencias ≤ {money(tolerancia)} se absorben automáticamente (sobrante/faltante de caja). Las mayores esperan resolución de un
            administrador: <span className="font-medium">faltante</span> → descuento por rol (CxC empleado) o gasto · <span className="font-medium">sobrante</span> → otros ingresos.
          </p>
        </CardContent>
      </Card>

      {resolver && <ResolverModal cierre={resolver} onClose={() => setResolver(null)} onOk={() => { setResolver(null); invalidar(); }} />}
      {depositar && <DepositoModal cierres={pendientesDeposito} onClose={() => setDepositar(false)} onOk={() => { setDepositar(false); invalidar(); }} />}
    </div>
  );
}

// ---- Resolver diferencia de arqueo (caso por caso) ----
function ResolverModal({ cierre, onClose, onOk }: { cierre: Cierre; onClose: () => void; onOk: () => void }) {
  const faltante = Number(cierre.diferencia) < 0;
  const [destino, setDestino] = useState(faltante ? 'rol' : 'ingreso');
  const [obs, setObs] = useState('');

  const resolver = useMutation({
    mutationFn: () => posRpc<{ anticipo_id: string | null; aviso: string | null }>('pos_dif_resolver', { _sesion_id: cierre.sesion_id, _destino: destino, _obs: obs || null }),
    onSuccess: (r) => {
      if (r?.anticipo_id) toast.success('Diferencia resuelta — anticipo de nómina creado: se descontará en el próximo rol del cajero');
      else toast.success('Diferencia resuelta');
      if (r?.aviso) toast.warning(r.aviso, { duration: 9000 });
      onOk();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Resolver diferencia · ${cierre.codigo}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="rounded-md border p-3 text-sm">
          <div className="flex justify-between"><span>Sistema</span><span className="font-mono">{money(cierre.efectivo_sistema)}</span></div>
          <div className="flex justify-between"><span>Contado por {cierre.cajero ?? 'el cajero'}</span><span className="font-mono">{money(cierre.efectivo_contado)}</span></div>
          <div className={`flex justify-between font-semibold ${faltante ? 'text-red-600' : 'text-emerald-600'}`}>
            <span>{faltante ? 'Faltante' : 'Sobrante'}</span><span className="font-mono">{money(Math.abs(Number(cierre.diferencia)))}</span>
          </div>
        </div>

        {faltante ? (
          <Campo label="Destino del faltante">
            <select className={fieldCls} value={destino} onChange={(e) => setDestino(e.target.value)}>
              <option value="rol">Descontar al cajero por rol (CxC empleado)</option>
              <option value="gasto">Asumir como gasto (faltante de caja)</option>
            </select>
          </Campo>
        ) : (
          <p className="text-sm text-muted-foreground">El sobrante queda registrado como <span className="font-medium">otros ingresos</span> (ya contabilizado al cierre); se deja constancia de la revisión.</p>
        )}
        {destino === 'rol' && (
          <p className="text-xs text-muted-foreground">Se reclasifica el gasto a <span className="font-medium">Anticipos/CxC empleados</span> a nombre del cajero. El descuento se aplica en su próximo rol desde Nómina (anticipos).</p>
        )}
        <Campo label="Observación"><input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Motivo / acta / autorización…" /></Campo>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={resolver.isPending} onClick={() => resolver.mutate()}>Confirmar resolución</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Registrar depósito bancario (multi-cierre) ----
function DepositoModal({ cierres, onClose, onOk }: { cierres: Cierre[]; onClose: () => void; onOk: () => void }) {
  const [sel, setSel] = useState<string[]>(cierres.map((c) => c.sesion_id));
  const [cuenta, setCuenta] = useState('');
  const [fecha, setFecha] = useState(hoy());
  const [referencia, setReferencia] = useState('');
  const [monto, setMonto] = useState('');
  const [obs, setObs] = useState('');

  const { data: cuentas = [] } = useQuery<CuentaBancaria[]>({
    queryKey: ['cb-cuentas-pos'],
    queryFn: async () => (await apiClient.get<CuentaBancaria[]>(
      '/rest/control_bancario.cuentas?select=id,numero,alias,banco:banco_id(nombre)&es_activo=eq.true&deleted_at=is.null&order=numero')).data,
  });

  const esperado = useMemo(() => cierres.filter((c) => sel.includes(c.sesion_id)).reduce((a, c) => a + Number(c.a_depositar || 0), 0), [cierres, sel]);
  const montoNum = monto === '' ? esperado : Number(monto) || 0;
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const registrar = useMutation({
    mutationFn: () => posRpc('pos_deposito_registrar', { payload: {
      cuenta_bancaria_id: cuenta, fecha, referencia: referencia || null,
      monto: montoNum, observaciones: obs || null, sesion_ids: sel,
    } }),
    onSuccess: (r: unknown) => {
      const d = r as { codigo: string; diferencia: number };
      toast.success(`Depósito ${d.codigo} registrado${Number(d.diferencia) !== 0 ? ` — diferencia ${money(d.diferencia)}` : ''}`);
      onOk();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Registrar depósito bancario" onClose={onClose}>
      <div className="space-y-3">
        <div className="max-h-44 overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card"><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-1.5 px-2 w-8"></th><th className="py-1.5 px-2">Cierre</th><th className="py-1.5 px-2">Caja</th><th className="py-1.5 px-2 text-right">A depositar</th>
            </tr></thead>
            <tbody>
              {cierres.map((c) => (
                <tr key={c.sesion_id} className="border-b last:border-0">
                  <td className="py-1.5 px-2"><input type="checkbox" checked={sel.includes(c.sesion_id)} onChange={() => toggle(c.sesion_id)} /></td>
                  <td className="py-1.5 px-2 font-mono text-xs">{c.codigo}</td>
                  <td className="py-1.5 px-2 text-xs">{c.caja}</td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums">{money(c.a_depositar)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Campo label="Cuenta bancaria *" className="col-span-2">
            <select className={fieldCls} value={cuenta} onChange={(e) => setCuenta(e.target.value)}>
              <option value="">— Selecciona —</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre ?? 'Banco'} · {c.numero}{c.alias ? ` (${c.alias})` : ''}</option>)}
            </select>
          </Campo>
          <Campo label="Fecha *"><input type="date" className={fieldCls} value={fecha} max={hoy()} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Campo label="N° papeleta / referencia"><input className={fieldCls} value={referencia} onChange={(e) => setReferencia(e.target.value)} /></Campo>
          <Campo label={`Monto depositado * (esperado: ${money(esperado)})`} className="col-span-2">
            <input type="number" step="0.01" min="0" className={`${fieldCls} text-right`} value={monto} placeholder={String(esperado.toFixed(2))} onChange={(e) => setMonto(e.target.value)} />
          </Campo>
          <Campo label="Observaciones" className="col-span-2"><input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} /></Campo>
        </div>

        {montoNum !== esperado && sel.length > 0 && (
          <p className={`text-xs font-medium ${montoNum < esperado ? 'text-red-600' : 'text-emerald-600'}`}>
            Diferencia de depósito: {money(montoNum - esperado)} — quedará visible en Efectivo en tránsito y en esta pantalla.
          </p>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={registrar.isPending} disabled={!cuenta || sel.length === 0 || montoNum <= 0} onClick={() => registrar.mutate()}>
            Registrar depósito
          </Button>
        </div>
      </div>
    </Modal>
  );
}
